import { makeStyles } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import Loader from 'src/component/Loader/Loader';
import { useUpdateProject } from 'src/reactQuery/projects/mutations/useUpdateProject';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import ProjectApps from './components/ProjectApps';
import UrlPrefixesSection from './components/UrlPrefixesSection';

const useStyle = makeStyles({
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '20px',
    marginLeft: '20px',
    textAlign: 'start'
  },
  wrapperDiv: {
    height: 'calc(100vh - 128px)',
    overflowY: 'scroll',
    padding: '24px'
  }
});

const DynamicLinksConfig: FC = () => {
  const classes = useStyle();

  const { data, refetch, isLoading } = useProject();

  const mutation = useUpdateProject({
    onSuccess() {
      refetch();
    }
  });

  const [apps, setApps] = useState<{ androidApps: AndroidApp[]; appleApps: AppleApp[] }>({
    androidApps: [],
    appleApps: []
  });

  const [urlPrefixes, setUrlPrefixes] = useState<{
    deepLinkPrefixes: string[];
    fallbackUrlPrefixes: string[];
    shortLinkPrefixes: string[];
  }>({
    deepLinkPrefixes: [],
    fallbackUrlPrefixes: [],
    shortLinkPrefixes: []
  });

  const handleUpdateApps = (updatedApps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => {
    if (!data) {
      return;
    }

    mutation.mutate({
      ...data,
      metadata: {
        ...(data.metadata ?? {}),
        dynamicLinks: {
          ...(data.metadata?.dynamicLinks ?? {}),
          ...updatedApps
        }
      }
    });
  };

  const handleUpdateUrlPrefixes = (urlPrefixes: {
    deepLinkPrefixes?: string[];
    fallbackUrlPrefixes?: string[];
    shortLinkPrefixes?: string[];
  }) => {
    if (!data) {
      return;
    }

    mutation.mutate({
      ...data,
      metadata: {
        ...(data.metadata ?? {}),
        dynamicLinks: {
          ...(data.metadata?.dynamicLinks ?? {}),
          ...urlPrefixes
        }
      }
    });
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const { androidApps, appleApps, deepLinkPrefixes, shortLinkPrefixes, fallbackUrlPrefixes } =
      data.metadata?.dynamicLinks ?? {};

    setApps({
      androidApps: androidApps ?? [],
      appleApps: appleApps ?? []
    });

    setUrlPrefixes({
      deepLinkPrefixes: deepLinkPrefixes ?? [],
      fallbackUrlPrefixes: fallbackUrlPrefixes ?? [],
      shortLinkPrefixes: shortLinkPrefixes ?? []
    });
  }, [data]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={classes.wrapperDiv}>
      <div className={classes.pageTitle}>Dynamic Links Config</div>

      <UrlPrefixesSection
        deepLinkPrefixes={urlPrefixes.deepLinkPrefixes}
        fallbackUrlPrefixes={urlPrefixes.fallbackUrlPrefixes}
        handleUpdateUrlPrefixes={handleUpdateUrlPrefixes}
        shortLinkPrefixes={urlPrefixes.shortLinkPrefixes}
      />

      <ProjectApps
        androidApps={apps.androidApps}
        appleApps={apps.appleApps}
        handleUpdateApps={handleUpdateApps}
      />
    </div>
  );
};

export default DynamicLinksConfig;
